import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { Camera, Link2, Loader2, RefreshCcw, Unlink } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import ClubNavbar from '../../components/ClubNavbar';
import { adminAPI } from '../../services/api';
import { toast } from 'sonner';

export default function ClubCameras() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [overview, setOverview] = useState([]);
  const [availableCameras, setAvailableCameras] = useState([]);
  const [selectedCameraByCourt, setSelectedCameraByCourt] = useState({});

  const fetchData = async () => {
    setLoading(true);
    try {
      const [assignmentsRes, availableRes] = await Promise.all([
        adminAPI.getCameraAssignments(),
        adminAPI.getAvailableCameras(),
      ]);

      setOverview(assignmentsRes.data?.overview || []);
      setAvailableCameras(availableRes.data?.cameras || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo cargar la gestión de cámaras');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const currentClub = useMemo(() => overview[0] || null, [overview]);

  const getCameraName = (cameraId) => {
    const camera = availableCameras.find((c) => c.id === cameraId);
    return camera?.name || cameraId;
  };

  const assignCamera = async (courtId) => {
    const cameraId = selectedCameraByCourt[courtId];
    if (!cameraId || !currentClub?.club_id) {
      toast.error('Selecciona una cámara');
      return;
    }

    setSaving(true);
    try {
      await adminAPI.assignCameraToCourtAPI(currentClub.club_id, courtId, cameraId);
      toast.success('Cámara asignada');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo asignar la cámara');
    } finally {
      setSaving(false);
    }
  };

  const unassignCamera = async (courtId, cameraId) => {
    if (!currentClub?.club_id) return;
    setSaving(true);
    try {
      await adminAPI.unassignCameraFromCourt(currentClub.club_id, courtId, cameraId || null);
      toast.success('Asignación eliminada');
      await fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo eliminar la asignación');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="min-h-screen bg-background pb-24 md:pb-8" data-testid="club-cameras-page">
      <ClubNavbar />

      <main className="mx-auto max-w-6xl p-4 md:p-6">
        <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}>
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-4xl font-bold">Cámaras por cancha</h1>
              <p className="text-sm text-muted-foreground" data-testid="club-cameras-subtitle">
                Gestiona cámaras de tu club y su asignación por cancha.
              </p>
            </div>
            <Button variant="outline" onClick={fetchData} disabled={loading || saving} data-testid="club-cameras-refresh-button">
              <RefreshCcw className="mr-2 h-4 w-4" />
              Actualizar
            </Button>
          </div>

          {loading ? (
            <div className="flex items-center justify-center rounded-xl border border-border py-14" data-testid="club-cameras-loading-state">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : !currentClub ? (
            <Card data-testid="club-cameras-empty-state">
              <CardContent className="py-10 text-center text-muted-foreground">
                No se encontró un club asignado para esta cuenta.
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4" data-testid="club-cameras-main-content">
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-lg">
                    <Camera className="h-5 w-5" />
                    {currentClub.club_name}
                  </CardTitle>
                </CardHeader>
              </Card>

              <div className="grid gap-4 md:grid-cols-2" data-testid="club-cameras-courts-grid">
                {currentClub.courts?.map((court) => (
                  <Card key={court.court_id} data-testid={`club-cameras-court-card-${court.court_id}`}>
                    <CardHeader>
                      <CardTitle className="text-base">Cancha {court.court_number}</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="rounded-md bg-secondary/40 p-3 text-sm" data-testid={`club-cameras-current-${court.court_id}`}>
                        {court.cameras?.length > 0 ? (
                          <div className="space-y-2">
                            {court.cameras.map((cam) => (
                              <div key={cam.camera_id} className="flex items-center justify-between gap-2">
                                <span className="truncate">{cam.name || cam.camera_id}</span>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => unassignCamera(court.court_id, cam.camera_id)}
                                  disabled={saving}
                                  data-testid={`club-cameras-unassign-${court.court_id}-${cam.camera_id}`}
                                >
                                  <Unlink className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <span className="text-muted-foreground">Sin cámara asignada</span>
                        )}
                      </div>

                      <div className="grid gap-2 sm:grid-cols-[1fr_auto]">
                        <Select
                          value={selectedCameraByCourt[court.court_id] || ''}
                          onValueChange={(value) =>
                            setSelectedCameraByCourt((prev) => ({ ...prev, [court.court_id]: value }))
                          }
                        >
                          <SelectTrigger data-testid={`club-cameras-select-${court.court_id}`}>
                            <SelectValue placeholder="Selecciona cámara" />
                          </SelectTrigger>
                          <SelectContent>
                            {availableCameras.map((camera) => (
                              <SelectItem key={camera.id} value={camera.id}>
                                {getCameraName(camera.id)} {camera.is_assigned ? '(asignada)' : ''}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          onClick={() => assignCamera(court.court_id)}
                          disabled={saving}
                          data-testid={`club-cameras-assign-${court.court_id}`}
                        >
                          <Link2 className="mr-2 h-4 w-4" />
                          Asignar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}
        </motion.div>
      </main>
    </div>
  );
}
